import React, { useCallback, useMemo } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import { AutoSizer } from 'react-virtualized/dist/es/AutoSizer'
import { SortDirection, Table } from 'react-virtualized/dist/es/Table'
import _ from 'lodash'

const TableContainer = styled.div`
  flex: 1 1 auto;
  overflow: hidden;
`

const Placeholder = styled.div`
  color: ${p => p.theme.palette.neutralSecondary};
  text-align: center;
`

const refsGetter = (tableRef, innerRef) => comp => {
  if (_.isFunction(tableRef)) {
    tableRef(comp)
  }
  const div = ReactDOM.findDOMNode(comp)
  if (_.isFunction(innerRef)) {
    innerRef(div)
  }
}

const useLocalClassNames = () => {
  return useMemo(() => {
    return {
      headerRow: 'box-border border-b border-pm-neutral-light',
      anyRow: 'cursor-pointer hover:bg-pm-neutral-light box-border',
      selectedRow: 'cursor-pointer bg-pm-neutral-lighter-alt box-border',
    }
  }, [])
}

export const VirtualizedTable = ({
  headerHeight = 30,
  rowHeight = 44,
  overscanRowCount = 10,
  rows,
  selectedRow,
  noRowsRenderer,
  onRowsRendered,
  onRowClick,
  onRowRightClick,
  scrollToIndex,
  sort,
  sortBy,
  sortDirection = SortDirection.ASC,
  loading = false,
  shimmersCount = 5,
  placeholder,
  tableRef,
  innerTableRef,
  children,
  rowRenderer,
  ...rest
}) => {
  const classNames = useLocalClassNames()
  const rowGetter = useCallback(
    ({ index }) => {
      return rows[index]
    },
    [rows]
  )

  const noRowsRendererByDefault = useCallback(() => {
    return <Placeholder>{placeholder}</Placeholder>
  }, [placeholder])

  const noRowsRendererIfNeeded = useMemo(() => {
    if (placeholder) {
      return noRowsRendererByDefault
    }
    return noRowsRenderer
  }, [noRowsRenderer, noRowsRendererByDefault, placeholder])

  const rowClassName = useCallback(
    ({ index }) => {
      if (index < 0) {
        return classNames.headerRow
      } else if (selectedRow) {
        return rows[index] === selectedRow ? classNames.selectedRow : classNames.anyRow
      } else {
        return classNames.anyRow
      }
    },
    [selectedRow, classNames.headerRow, classNames.selectedRow, classNames.anyRow, rows]
  )

  const rowsCount = loading ? rows.length + shimmersCount : rows.length
  return (
    <TableContainer {...rest}>
      <AutoSizer>
        {({ width, height }) => (
          <Table
            headerHeight={headerHeight}
            height={height}
            width={width}
            rowHeight={rowHeight}
            overscanRowCount={overscanRowCount}
            rowGetter={rowGetter}
            onRowsRendered={onRowsRendered}
            noRowsRenderer={noRowsRendererIfNeeded}
            onRowClick={onRowClick}
            onRowRightClick={onRowRightClick}
            rowCount={rowsCount}
            rowClassName={rowClassName}
            scrollToIndex={scrollToIndex}
            sort={sort}
            sortBy={sortBy}
            sortDirection={sortDirection}
            rowRenderer={rowRenderer}
            ref={refsGetter(tableRef, innerTableRef)}
          >
            {children}
          </Table>
        )}
      </AutoSizer>
    </TableContainer>
  )
}
